import React, { useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import { ToastContainer } from "react-toastify";
import { BtnSubmit } from "../../../../components/button/button-submit/button-submit";
import { BtnCustom } from "../../../../components/button/button";
import TextInput from "../../../../components/inputs/text";
import SelectInput from "../../../../components/inputs/select";
import NumberInput from "../../../../components/inputs/number";
import MaskInput from "../../../../components/inputs/mask";
import Loading from "../../../../components/loading";
import AlertMessage from "../../../../components/alert-message";
import libForm from "../../../../util/libForm";
import { IndexHook, AMBIENTES, REGIMES } from "./hooks";

const estiloAba = { fontSize: "0.775rem" };

// Sem certificado nao existe registro no banco, entao nao ha o que editar.
// O fieldset desabilita tudo que esta dentro sem passar prop campo a campo.
const Campos = ({ bloqueado, comAviso = true, children }) => (
	<>
		{bloqueado && comAviso ? (
			<AlertMessage type="warning">
				Envie o certificado na aba Emitente para liberar o cadastro.
			</AlertMessage>
		) : null}

		<fieldset disabled={bloqueado} className="m-0 min-w-0 border-0 p-0">
			{children}
		</fieldset>
	</>
);

const BlocoDivergencia = ({ divergencia, enviandoCert, onConfirmar, onCancelar }) => (
	<div className="col-12">
		<AlertMessage type="warning" title="O certificado é de outro CNPJ">
			No cadastro está <strong>{divergencia.cnpj_cadastro}</strong> e no certificado{" "}
			<strong>{divergencia.cnpj_certificado}</strong>, de {divergencia.razao_certificado}.
			<div className="mt-2">
				Atualizar troca o CNPJ e a razão social do cadastro. Notas já emitidas continuam com os dados
				antigos.
			</div>
			<div className="d-flex gap-2 mt-3">
				<BtnCustom
					className="btn btn-sm btn-warning"
					isSubmitting={enviandoCert}
					onClick={onConfirmar}
					text="Atualizar cadastro"
				/>
				<BtnCustom
					className="btn btn-sm btn-outline-secondary"
					onClick={onCancelar}
					text="Cancelar"
				/>
			</div>
		</AlertMessage>
	</div>
);

const PainelCertificado = ({
	certificado,
	arquivo,
	senha,
	setSenha,
	enviandoCert,
	divergencia,
	erroCertificado,
	handleSelecionarArquivo,
	handleEnviarCertificado,
	handleCancelarTroca,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const showUpload = !certificado || isEditing;

	// So fecha o painel quando o servidor aceitou o certificado. Recusado,
	// o formulario fica no lugar com o motivo e a senha pode ser corrigida.
	const enviar = async (confirmar) => {
		if (await handleEnviarCertificado(confirmar)) setIsEditing(false);
	};

	const cancelar = () => {
		handleCancelarTroca();
		setIsEditing(false);
	};

	return (
		<div className="row">
			<div className="col-12">
				{certificado ? (
					<AlertMessage type="success" title="Certificado ativo">
						<div>
							<strong>{certificado.titular}</strong>
						</div>
						<div>CNPJ {certificado.cnpj}</div>
						<div>Válido até {new Date(certificado.validade).toLocaleDateString("pt-BR")}</div>
						<div>Série {certificado.numero_serie}</div>
					</AlertMessage>
				) : (
					<AlertMessage type="warning" title="Nenhum certificado enviado">
						Sem ele a emissão de NFC-e fica bloqueada. Use um certificado A1, em arquivo .pfx.
					</AlertMessage>
				)}
			</div>

			{certificado?.bloqueado ? (
				<div className="col-12">
					<AlertMessage type="error" title="Emissão bloqueada">
						{certificado.bloqueio_motivo}
					</AlertMessage>
				</div>
			) : null}

			{divergencia ? (
				<BlocoDivergencia
					divergencia={divergencia}
					enviandoCert={enviandoCert}
					onConfirmar={() => enviar(true)}
					onCancelar={cancelar}
				/>
			) : null}

			{showUpload && erroCertificado ? (
				<div className="col-12">
					<AlertMessage type="error" title="O certificado não foi aceito">
						{erroCertificado}
						{certificado ? (
							<div className="mt-2">Nada foi alterado: o certificado atual continua valendo.</div>
						) : null}
					</AlertMessage>
				</div>
			) : null}

			{!showUpload ? (
				<div className="col-12 d-flex justify-content-end mt-3">
					<BtnCustom
						className="btn btn-sm btn-primary"
						onClick={() => setIsEditing(true)}
						text="Anexar outro certificado"
					/>
				</div>
			) : null}

			{showUpload ? (
				<div className="col-12">
					<div className="border rounded p-3 mt-3">
						<div className="d-flex align-items-center justify-content-between mb-3">
							<strong>{certificado ? "Trocar certificado" : "Upload do certificado"}</strong>

							{certificado ? (
								<BtnCustom
									className="btn btn-sm btn-outline-secondary"
									onClick={cancelar}
									text="Cancelar"
								/>
							) : null}
						</div>

						<div className="row">
							<div className="col-12 col-lg-8 mb-3">
								<label
									className="d-flex flex-column align-items-center justify-content-center w-100 rounded border-2 border-dashed border-customBlue-100 p-4 text-customBlue-500"
									style={{ cursor: "pointer" }}
								>
									<svg
										viewBox="0 0 24 24"
										style={{
											width: "32px",
											height: "32px",
											fill: "none",
											stroke: "currentColor",
											strokeWidth: "2",
										}}
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
										/>
									</svg>

									<span className="mt-2" style={{ fontSize: "14px" }}>
										<strong>Clique para selecionar</strong> ou arraste o arquivo
									</span>
									<span className="text-muted" style={{ fontSize: "12px" }}>
										Apenas arquivos .pfx ou .p12
									</span>

									<input
										key={arquivo?.name ?? "vazio"}
										type="file"
										accept=".pfx,.p12"
										onChange={handleSelecionarArquivo}
										className="d-none"
									/>
								</label>

								{arquivo ? (
									<p className="text-success mb-0 mt-2" style={{ fontSize: "14px" }}>
										Arquivo selecionado: <strong>{arquivo.name}</strong>
									</p>
								) : null}
							</div>

							<div className="col-12 col-lg-4 d-flex flex-column justify-content-end">
								<div className="input-box">
									<label htmlFor="senha_certificado">Senha do arquivo</label>
									<input
										id="senha_certificado"
										type="password"
										value={senha}
										onChange={(event) => setSenha(event.target.value)}
										onKeyDown={libForm.EnterTab}
										autoComplete="new-password"
									/>
								</div>

								<BtnCustom
									className="btn btn-success mt-2"
									isSubmitting={enviandoCert}
									onClick={() => enviar(false)}
									text={enviandoCert ? "Validando" : "Validar certificado"}
								/>
							</div>
						</div>
					</div>
				</div>
			) : null}
		</div>
	);
};

const FormFiscalConfig = () => {
	const {
		register,
		handleSubmit,
		handleOnSubmit,
		control,
		errors,
		isSubmitting,
		loading,
		valueTab,
		handleChangeTab,
		cepIsSubmitting,
		handleFetchAddress,
		handleDigitarIE,
		certificado,
		arquivo,
		senha,
		setSenha,
		enviandoCert,
		divergencia,
		erroCertificado,
		handleSelecionarArquivo,
		handleEnviarCertificado,
		handleCancelarTroca,
		handleOnError,
		handleNextCertificado,
	} = IndexHook();

	if (loading)
		return (
			<div className="flex h-full">
				<Loading mensagem="Aguarde buscando dados no servidor..." />
			</div>
		);

	return (
		<div className="container-forms">
			<ToastContainer />

			<form className="form" onSubmit={handleSubmit(handleOnSubmit, handleOnError)} autoComplete="off">
				<div className="flex flex-grow flex-col overflow-y-auto">
					<TabContext value={valueTab}>
						<Box>
							<TabList onChange={handleChangeTab}>
								<Tab label="Certificado" value="0" sx={estiloAba} />
								<Tab label="Emitente" disabled={!certificado} value="1" sx={estiloAba} />
								<Tab label="Documento" disabled={!certificado} value="2" sx={estiloAba} />
								<Tab label="Impostos" disabled={!certificado} value="3" sx={estiloAba} />
								<Tab label="IBS / CBS" disabled={!certificado} value="4" sx={estiloAba} />
							</TabList>
						</Box>

						<main>
							<TabPanel value="0">
								<PainelCertificado
									certificado={certificado}
									arquivo={arquivo}
									senha={senha}
									setSenha={setSenha}
									enviandoCert={enviandoCert}
									divergencia={divergencia}
									erroCertificado={erroCertificado}
									handleSelecionarArquivo={handleSelecionarArquivo}
									handleEnviarCertificado={handleEnviarCertificado}
									handleCancelarTroca={handleCancelarTroca}
								/>

								<div className="d-flex justify-content-end mt-3">
									<BtnCustom
										className="btn btn-sm btn-outline-primary"
										onClick={handleNextCertificado}
										text="Próximo: Dados do Emitente ➔"
									/>
								</div>
							</TabPanel>

							<TabPanel value="1">
								<Campos bloqueado={!certificado} comAviso={false}>
									<div className="row">
										<div className="col-12">
											<AlertMessage type="info" title="1. Dados do Emitente">
												Puxamos a maioria dos dados do seu certificado, mas você precisa informar seu <strong>CEP</strong> e o <strong>Regime Tributário</strong> corretamente.
											</AlertMessage>
										</div>

										<MaskInput
											label="CNPJ"
											name="cnpj"
											control={control}
											errors={errors}
											format="##.###.###/####-##"
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-3"
										/>
										<TextInput
											label="Inscrição Estadual"
											id="ie"
											inputMode="numeric"
											maxLength={20}
											register={register}
											errors={errors}
											onChange={handleDigitarIE}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-3"
										/>
										<TextInput
											label="Razão Social"
											id="razao_social"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-6"
										/>
										<TextInput
											label="Nome Fantasia"
											id="fantasia"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-6"
										/>
										<MaskInput
											label="Telefone"
											name="telefone"
											control={control}
											errors={errors}
											format="(##) #####-####"
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-3"
										/>
										<SelectInput
											label="Regime Tributário"
											id="crt"
											labelSelect="nome"
											control={control}
											data={REGIMES}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-3"
										/>

										<MaskInput
											label="CEP"
											name="cep"
											control={control}
											errors={errors}
											format="#####-###"
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<div className="col-12 col-lg-3 d-flex align-items-center">
											<BtnCustom
												className="btn btn-sm btn-outline-primary btn-cep"
												onKeyDown={libForm.EnterTab}
												onClick={() => handleFetchAddress()}
												text={cepIsSubmitting ? "Buscando..." : "Buscar endereço por CEP"}
											/>
										</div>
										<TextInput
											label="Logradouro"
											id="rua"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-6"
										/>
										<TextInput
											label="Número"
											id="numero"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="Complemento"
											id="complemento"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="Bairro"
											id="bairro"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-4"
										/>
										<TextInput
											label="Cidade"
											id="cidade"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-4"
										/>
										<TextInput
											label="Código IBGE"
											id="cod_cidade"
											placeholder="Ex.: 3516200"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="UF"
											id="uf"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										
										<div className="col-12 d-flex justify-content-end mt-3">
									<BtnCustom
										className="btn btn-sm btn-outline-primary"
										onClick={() => handleChangeTab(null, "2")}
										text="Próximo: Emissão de Notas (NFC-e) ➔"
									/>
								</div>
									</div>
								</Campos>
							</TabPanel>

							<TabPanel value="2">
								<Campos bloqueado={!certificado}>
									<div className="row">
										<div className="col-12">
											<AlertMessage type="info" title="2. Emissão de Notas">
												Configure o ambiente (Homologação para testes, Produção para valer) e os dados do CSC (Código de Segurança do Contribuinte) da sua SEFAZ.
											</AlertMessage>
										</div>

										<SelectInput
											label="Ambiente"
											id="ambiente"
											labelSelect="nome"
											control={control}
											data={AMBIENTES}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-3"
										/>
										<NumberInput
											label="Série"
											name="serie"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<NumberInput
											label="Timeout (ms)"
											name="timeout"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="Id do CSC"
											id="id_csc"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="CSC"
											id="csc"
											placeholder="Código de Segurança do Contribuinte"
											toUpperCase={false}
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-6"
										/>
										
										<div className="col-12 d-flex justify-content-end mt-3">
									<BtnCustom
										className="btn btn-sm btn-outline-primary"
										onClick={() => handleChangeTab(null, "3")}
										text="Próximo: Impostos Padrão ➔"
									/>
								</div>
									</div>
								</Campos>
							</TabPanel>

							<TabPanel value="3">
								<Campos bloqueado={!certificado}>
									<div className="row">
										<div className="col-12">
											<AlertMessage type="info" title="3. Padrão de Impostos">
												Estes impostos agirão como um sistema de <strong>reserva (fallback)</strong>. Eles só serão usados caso você não cadastre regras tributárias específicas nos seus produtos.
											</AlertMessage>
										</div>

										<TextInput
											label="CFOP padrão"
											id="cfop_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="CSOSN padrão"
											id="csosn_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="CST padrão"
											id="cst_padrao"
											placeholder="Só para Regime Normal"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<NumberInput
											label="Alíquota de ICMS (%)"
											name="icms_padrao"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="NCM padrão"
											id="ncm_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="CEST padrão"
											id="cest_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>

										<div className="col-12 d-flex justify-content-end mt-3">
									<BtnCustom
										className="btn btn-sm btn-outline-primary"
										onClick={() => handleChangeTab(null, "4")}
										text="Próximo: Reforma Tributária ➔"
									/>
								</div>
									</div>
								</Campos>
							</TabPanel>

							<TabPanel value="4">
								<Campos bloqueado={!certificado}>
									<div className="row">
										<div className="col-12">
											<AlertMessage type="info" title="4. Reforma Tributária (IBS / CBS)">
												Deixe CST e cClassTrib vazios para não gerar o grupo na nota. O Simples Nacional só é obrigado a partir de 2027.
											</AlertMessage>
										</div>

										<TextInput
											label="CST IBS/CBS"
											id="cst_ibscbs_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<TextInput
											label="cClassTrib"
											id="cclasstrib_padrao"
											register={register}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<NumberInput
											label="IBS UF (%)"
											name="pibsuf"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<NumberInput
											label="IBS Município (%)"
											name="pibsmun"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
										<NumberInput
											label="CBS (%)"
											name="pcbs"
											control={control}
											errors={errors}
											onKeyDown={libForm.EnterTab}
											className="col-12 col-lg-2"
										/>
									</div>
								</Campos>
							</TabPanel>
						</main>
					</TabContext>
				</div>

				{valueTab !== "0" && (
					<footer>
						<Campos bloqueado={!certificado} comAviso={false}>
							<BtnSubmit isSubmitting={isSubmitting} />
						</Campos>
					</footer>
				)}
			</form>
		</div>
	);
};

export default FormFiscalConfig;
